import React, { useState, useEffect, useRef } from 'react';
import ReactGA from 'react-ga';
import propTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import config from '~/Config';
import Button from '~/Components/Global/Form/Button';
import history from '~/Providers/History';
import moment from '~/Providers/Moment';

import Card from '~/Components/Layout/Card/Card';
import Form from '~/Components/Global/Form/Form';
import TextInput from '~/Components/Global/Form/TextInput';
import ArtfulCheckbox from '~/Components/Global/Form/ArtfulCheckbox';
import { useMountEffect } from '~/util';
import { AccountApi, errorApi } from '~/Providers/Api';

import ConsentRequiredView, {
  CONSENT_TYPE_EMAIL,
} from '~/Components/Global/ConsentRequiredView/ConsentRequiredView';
import clevertapProvider, { ScreenViewEvent } from '~/Providers/CleverTap';
import Store from '~/Redux/Store';
import { error } from '~/Redux/Modules/Notifications';

import './Register.scss';
import GoogleSignInWrapper from '~/Components/Global/GoogleSignInWrapper/GoogleSignInWrapper';
import { useConsentSettings } from '~/Components/Global/ConsentRequiredView';
import AppleSignInButton from '~/Components/Global/AppleSignInButton/AppleSignInButton';
import Storage from '~/Providers/Storage';
import mobileImage1 from '~/Components/Pages/Register/registration_carousel_mobile_1.png';
import mobileImage2 from '~/Components/Pages/Register/registration_carousel_mobile_2.png';
import mobileImage3 from '~/Components/Pages/Register/registration_carousel_mobile_3.png';
import mobileImage4 from '~/Components/Pages/Register/registration_carousel_mobile_4.png';
import mobileImage5 from '~/Components/Pages/Register/registration_carousel_mobile_5.png';
import mobileImage6 from '~/Components/Pages/Register/registration_carousel_mobile_6.png';
import mobileImage7 from '~/Components/Pages/Register/registration_carousel_mobile_7.png';
import tabletImage1 from '~/Components/Pages/Register/registration_carousel_large_mobile_1.png';
import tabletImage2 from '~/Components/Pages/Register/registration_carousel_large_mobile_2.png';
import tabletImage3 from '~/Components/Pages/Register/registration_carousel_large_mobile_3.png';
import tabletImage4 from '~/Components/Pages/Register/registration_carousel_large_mobile_4.png';
import tabletImage5 from '~/Components/Pages/Register/registration_carousel_large_mobile_5.png';
import tabletImage6 from '~/Components/Pages/Register/registration_carousel_large_mobile_6.png';
import tabletImage7 from '~/Components/Pages/Register/registration_carousel_large_mobile_7.png';
import webImage1 from '~/Components/Pages/Register/registration_carousel_web_1.png';
import webImage2 from '~/Components/Pages/Register/registration_carousel_web_2.png';
import webImage3 from '~/Components/Pages/Register/registration_carousel_web_3.png';
import webImage4 from '~/Components/Pages/Register/registration_carousel_web_4.png';
import webImage5 from '~/Components/Pages/Register/registration_carousel_web_5.png';
import webImage6 from '~/Components/Pages/Register/registration_carousel_web_6.png';
import webImage7 from '~/Components/Pages/Register/registration_carousel_web_7.png';
import Quote from '~/Components/Global/Assets/Svgs/Quote';
import Spinner from '~/Components/Global/Loading/Spinner';

const webImages = [webImage1, webImage2, webImage3, webImage4, webImage5, webImage6, webImage7];
const tabletPortraitImages = [
  tabletImage1,
  tabletImage2,
  tabletImage3,
  tabletImage4,
  tabletImage5,
  tabletImage6,
  tabletImage7,
];
const mobileImages = [
  mobileImage1,
  mobileImage2,
  mobileImage3,
  mobileImage4,
  mobileImage5,
  mobileImage6,
  mobileImage7,
];

const ImageCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 767);
  const [isTabletPortrait, setIsTabletPortrait] = useState(
    window.innerWidth >= 767 && window.innerWidth < 1024
  );
  const autoAdvanceRef = useRef(null);
  const userInteractedRef = useRef(false);
  const resetTimeoutRef = useRef(null);

  const reviews = useSelector(state => state.Reviews.reviewCollection.data || []);
  const currentImages = isMobile
    ? mobileImages
    : isTabletPortrait
    ? tabletPortraitImages
    : webImages;

  const imageReviewPairs = Array.isArray(reviews)
    ? reviews.map((review, index) => ({
        image: currentImages[index % currentImages.length],
        review: review,
      }))
    : [];

  //Setup resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 767);
      setIsTabletPortrait(window.innerWidth >= 767 && window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (currentIndex >= imageReviewPairs.length && imageReviewPairs.length > 0) {
      setCurrentIndex(0);
    } else if (imageReviewPairs.length === 0 && currentIndex !== 0) {
      setCurrentIndex(0);
    }
  }, [currentIndex, imageReviewPairs.length]);

  // Auto-advance functionality
  useEffect(() => {
    if (!userInteractedRef.current && imageReviewPairs.length > 0) {
      autoAdvanceRef.current = setInterval(() => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % imageReviewPairs.length);
      }, 8000);
    }
    return () => clearInterval(autoAdvanceRef.current);
  }, [currentIndex, userInteractedRef.current, imageReviewPairs.length]);

  const handleNext = () => {
    userInteractedRef.current = true;
    clearInterval(autoAdvanceRef.current);
    clearTimeout(resetTimeoutRef.current);

    if (imageReviewPairs.length > 0) {
      setCurrentIndex(prevIndex => (prevIndex + 1) % imageReviewPairs.length);
    }

    resetAutoAdvance();
  };

  const handlePrevious = () => {
    userInteractedRef.current = true;
    clearInterval(autoAdvanceRef.current);
    clearTimeout(resetTimeoutRef.current);

    if (imageReviewPairs.length > 0) {
      setCurrentIndex(prevIndex => (prevIndex === 0 ? imageReviewPairs.length - 1 : prevIndex - 1));
    }

    resetAutoAdvance();
  };

  // Reset auto-advance after manual interaction
  const resetAutoAdvance = () => {
    resetTimeoutRef.current = setTimeout(() => {
      userInteractedRef.current = false;
      restartAutoAdvance();
    }, 10000);
  };

  // Function to restart auto-advance after timeout
  const restartAutoAdvance = () => {
    clearInterval(autoAdvanceRef.current);
    autoAdvanceRef.current = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % imageReviewPairs.length);
    }, 8000);
  };

  if (imageReviewPairs.length === 0 || !imageReviewPairs) {
    return <Spinner />;
  }

  return (
    <div className="image-carousel">
      {imageReviewPairs[currentIndex] && (
        <img
          src={imageReviewPairs[currentIndex].image || currentImages[currentIndex]}
          alt={`Slide ${currentIndex + 1}`}
          className="carousel-image"
        />
      )}
      <div className="review-parent">
        <div className="review">
          <Quote className="review-quotation-mark" />
          {imageReviewPairs[currentIndex] && (
            <p className="review-review">{imageReviewPairs[currentIndex].review?.review}</p>
          )}
          {imageReviewPairs[currentIndex] && (
            <p className="review-name">{imageReviewPairs[currentIndex].review.author}</p>
          )}
          <div className="dots-container">
            {imageReviewPairs.map((_, index) => (
              <span key={index} className={`dot ${index === currentIndex ? 'active' : ''}`} />
            ))}
          </div>
        </div>
      </div>
      {/* Left clickable area */}
      <div
        onClick={handlePrevious}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '50%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0)', // Transparent
          cursor: 'pointer',
        }}
      />

      {/* Right clickable area */}
      <div
        onClick={handleNext}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '50%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0)', // Transparent
          cursor: 'pointer',
        }}
      />
    </div>
  );
};

const ordinalNumbers = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
];

const useRegisterCode = referralCode => {
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const interval = useRef();
  const request = useRef();

  useEffect(() => {
    if (interval.current) clearTimeout(interval.current);
    interval.current = setTimeout(() => {
      const stamp = moment().unix();
      request.current = stamp;
      setError(null);
      AccountApi.checkCode(referralCode)
        .then(results => {
          if (request.current !== stamp) {
            console.log(`discard results for ${referralCode}`, stamp, request.current);
            return;
          }
          console.log(`results for ${referralCode}`, results.data);
          setResults(results.data);
          setError(null);
        })
        .catch(error => {
          if (request.current !== stamp) {
            console.log(`discard error for ${referralCode}`, stamp, request.current);
            return;
          }
          console.log(`error for ${referralCode}`, error);
          setResults(null);
          setError(error);
        });
    }, 300);
  }, [referralCode]);

  return { results, error };
};

const Register = ({
  handleRegistration,
  updateField,
  updateSavedCoupon,
  fieldsRegister,
  isFetching,
  handleGoogleRegistration,
  handleAppleRegistration,
}) => {
  const handleRegister = () => {
    handleRegistration(fieldsRegister);
  };

  useMountEffect(() => {
    if (config.gaProperty) {
      ReactGA.ga(tracker => {
        console.log(`tracker: ${tracker.get('clientId')}`);
        updateField({ gaClientId: tracker.get('clientId') });
      });
    }

    clevertapProvider.logEvent(new ScreenViewEvent('Registration'));
  });

  useMountEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (search.has('promoCode')) {
      updateSavedCoupon(search.get('promoCode'));
    }
  });

  const consentSettings = useConsentSettings();
  useEffect(() => {
    if (consentSettings) {
      updateField({
        [CONSENT_TYPE_EMAIL]: consentSettings[CONSENT_TYPE_EMAIL],
        consent_country: consentSettings.country,
      });
    }
  }, [consentSettings, updateField]);

  const referralCode = fieldsRegister?.referral_code;

  const { results: registerCodeSettings, error: registerCodeError } = useRegisterCode(referralCode);

  const renderTrialInfo = () => {
    if (
      fieldsRegister.referral_code.toLowerCase() === 'sparkle19' ||
      fieldsRegister.referral_code.toLowerCase() === 'artfulmedia'
    )
      return (
        <>
          Try Artful Agenda <strong>FREE</strong> for three months!
        </>
      );

    if (registerCodeSettings && registerCodeSettings.trial_without_card) {
      let amount = registerCodeSettings.trial_without_card?.amount;
      if (amount < ordinalNumbers.length) amount = ordinalNumbers[amount];
      return (
        <>
          Try Artful Agenda <strong>FREE</strong> for {amount}{' '}
          {registerCodeSettings.trial_without_card?.units}!
        </>
      );
    }
    return (
      <>
        Try Artful Agenda for <strong>FREE</strong>!
      </>
    );
  };

  const renderNewStartButton = () => {
    let buttonText = 'START MY TRIAL';
    if (
      fieldsRegister.referral_code.toLowerCase() === 'sparkle19' ||
      fieldsRegister.referral_code.toLowerCase() === 'artfulmedia'
    )
      buttonText = 'Start Planning';

    if (registerCodeSettings?.trial_without_card) buttonText = 'Start Planning';
    return (
      <Button
        type="submit"
        text={buttonText}
        actionText="Signing Up"
        isFetching={isFetching}
        disabled={isFetching}
        className="btn btn-default btn-artful new-registration-button"
      />
    );
  };

  const renderStartButton = () => {
    let buttonText = 'START MY TRIAL';
    if (
      fieldsRegister.referral_code.toLowerCase() === 'sparkle19' ||
      fieldsRegister.referral_code.toLowerCase() === 'artfulmedia'
    )
      buttonText = 'START PLANNING';

    if (registerCodeSettings?.trial_without_card) buttonText = 'START PLANNING';
    return (
      <Button
        type="submit"
        text={buttonText}
        actionText="SIGNING UP"
        isFetching={isFetching}
        disabled={isFetching}
        className="btn btn-default btn-artful"
      />
    );
  };

  const renderRegisterCodeError = () => {
    if (registerCodeError?.status === 422) {
      return <div className="text-error center">That registration code is no longer valid</div>;
    } else if (registerCodeError?.status === 404) {
      return <div className="text-error center">That registration code was not found</div>;
    } else if (registerCodeError) {
      return (
        <div className="text-error center">An error occurred checking the registration code</div>
      );
    }
    return null;
  };

  const handleGoogleRegisterSuccess = response => {
    console.info('handleGoogleSuccess', response.code);
    let marketingOptOut = fieldsRegister.marketing_opt_out || fieldsRegister.marketing_opt_out_top;
    handleGoogleRegistration({
      code: response.code,
      referral_code: fieldsRegister.referral_code,
      marketing_opt_out: marketingOptOut,
      consent_country: consentSettings?.country,
    });
  };

  const handleGoogleRegisterFailed = err => {
    console.error('handleGoogleFailed', JSON.stringify(err.error));
    errorApi.noticeError(err, 'handleGoogleRegisterFailed');
    Store.dispatch(error('Sorry, an error occurred', err.error));
  };

  const handleAppleSuccess = response => {
    console.info('handleAppleSuccess', response);
    if (response.user) {
      console.log('found apple user', response.user);
      Storage.set('appleIdUser', JSON.stringify(response.user));
    }
    const user = response.user || JSON.parse(Storage.get('appleIdUser')) || {};
    let marketingOptOut = fieldsRegister.marketing_opt_out || fieldsRegister.marketing_opt_out_top;
    handleAppleRegistration({
      code: response.authorization.code,
      referral_code: fieldsRegister.referral_code,
      marketing_opt_out: marketingOptOut,
      consent_country: consentSettings?.country,
      name: user?.name?.firstName,
      last_name: user?.name?.lastName,
    });
  };

  const handleAppleFailed = err => {
    console.error('handleAppleFailed', JSON.stringify(err.error));
    errorApi.noticeError(err, 'handleAppleFailed');
    Store.dispatch(error('Sorry, an error occurred', err.error));
  };

  const inAppBrowser = () => {
    return (
      window.navigator.userAgent.indexOf('FBAN') !== -1 ||
      window.navigator.userAgent.indexOf('FBAV') !== -1 ||
      window.navigator.userAgent.indexOf('Bytedance') !== -1
    );
  };

  return (
    <div className="new-registration-container">
      <div className="new-registration-image-container">
        <ImageCarousel />
      </div>
      <div className="new-registration-card-container">
        <Card
          footerAction={() => <Link to="/login">Click here to login</Link>}
          footerText="Already have an account?"
          noLogo
        >
          <h2 className="center">{renderTrialInfo()}</h2>
          <div className="referral-container">
            <div className="referral-text">
              <p>Have a referral code?</p>
              <p>Enter it here first!</p>
            </div>
            <TextInput
              className="minimal-input mobile-font-fix new-registration-referral"
              onChange={updateField}
              placeholder="Enter code"
              name="referral_code"
              showLabel={false}
              value={fieldsRegister.referral_code}
            />
          </div>
          {!inAppBrowser() && (
            <GoogleSignInWrapper
              actionText="Signing Up"
              isUpdating={isFetching}
              buttonText="Continue with Google"
              onSuccess={credentialResponse => {
                handleGoogleRegisterSuccess(credentialResponse);
                console.log(credentialResponse);
              }}
              onError={err => {
                handleGoogleRegisterFailed(err);
                console.log('Account Registration Failed', err);
              }}
            />
          )}
          <AppleSignInButton
            buttonText="Continue with Apple"
            actionText="Signing Up"
            isFetching={isFetching}
            onSuccess={handleAppleSuccess}
            onError={handleAppleFailed}
          />
          <ConsentRequiredView consentType={CONSENT_TYPE_EMAIL}>
            <div className="top-checkbox">
              <ArtfulCheckbox
                checklistStyle="small"
                label="Do not email me with updates, tips, or offers from Artful Agenda"
                value={fieldsRegister.marketing_opt_out_top}
                onChange={updateField}
                name="marketing_opt_out_top"
              />
            </div>
          </ConsentRequiredView>
          <div className="divider-wrapper">
            <div className="divider" />
            <p className="divider-text">or</p>
            <div className="divider" />
          </div>

          <Form handleSubmit={handleRegister} className="new-registration-form-block">
            <div className="new-registration-input-row">
              <TextInput
                className="minimal-input mobile-font-fix new-registration-field"
                onChange={updateField}
                placeholder="First name"
                name="name"
                showLabel={false}
                value={fieldsRegister.name}
              />
              <TextInput
                className="minimal-input mobile-font-fix new-registration-field"
                onChange={updateField}
                placeholder="Last name"
                name="last_name"
                showLabel={false}
                value={fieldsRegister.last_name}
              />
            </div>
            <TextInput
              className="minimal-input mobile-font-fix new-registration-field"
              onChange={updateField}
              placeholder="Email address"
              name="email"
              showLabel={false}
              type="email"
              value={fieldsRegister.email}
            />
            <div className="new-registration-input-row">
              <TextInput
                className="minimal-input mobile-font-fix new-registration-field"
                onChange={updateField}
                placeholder="Password"
                name="password"
                showLabel={false}
                type="password"
                value={fieldsRegister.password}
              />
              <TextInput
                className="minimal-input mobile-font-fix new-registration-field"
                onChange={updateField}
                placeholder="Confirm password"
                name="password_confirmation"
                showLabel={false}
                type="password"
                value={fieldsRegister.password_confirmation}
              />
            </div>
            {renderRegisterCodeError()}
            <ConsentRequiredView consentType={CONSENT_TYPE_EMAIL}>
              <div className="bottom-checkbox">
                <ArtfulCheckbox
                  checklistStyle="small"
                  label="Do not email me with updates, tips, or offers from Artful Agenda"
                  value={fieldsRegister.marketing_opt_out}
                  onChange={updateField}
                  name="marketing_opt_out"
                />
              </div>
            </ConsentRequiredView>
            <div className="center">{renderNewStartButton()}</div>
          </Form>
          <div className="disclosure new-registration-disclosure">
            {`By signing up, you agree to Artful Agenda's`}{' '}
            <a href="http://artfulagenda.com/terms" className="terms new-registration-terms">
              Terms of Service
            </a>
          </div>
          <div className="card-caption login-link-wrapper new-registration-caption">
            <small className="login-link-text">Already have an account?</small>
            <button
              onClick={() => history.push('/login')}
              className="login-link new-registration-login-link"
            >
              <small>Login</small>
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

Register.propTypes = {
  handleRegistration: propTypes.func,
  handleAppleRegistration: propTypes.func,
  handleGoogleRegistration: propTypes.func,
  updateSavedCoupon: propTypes.func,
  fieldsRegister: propTypes.object,
  updateField: propTypes.func,
  isFetching: propTypes.bool,
  checklistStyle: propTypes.bool,
};

Register.defaultProps = {};

export default Register;
